import { useEffect, useMemo, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import PlaceholderImage from '../../assets/images/image-place-holder.jpg';
import { useLocales } from '../../locales';
import * as icons from '../../config/icons';
import PropTypes from 'prop-types';
import './style.css';
import {
  getPosts,
  getNextPosts,
  changeRowsNumberPerPage,
  searchForPosts
} from '../../redux/slices/analytics/datatable';
import IsUpdatingIndicator from '../isUpdatingIndicator';
import Button from 'react-bootstrap/esm/Button';
import { OverlayTrigger, Stack, Tooltip } from 'react-bootstrap';
import { SHORT_ANALYTICS_TABLE_DEMO_DATA_RANGES_CONFIG } from '../../config/metrics';
import {
  COMMENTS,
  IMPRESSIONS,
  REACTIONS,
  SHARES
} from '../../config/constants';
import { PLATFORM_COLORS } from '../../config/icons';
import { LomavisDateTimeFormatter1 } from '@/lomavisSharedUi/atoms/utils/datetime/LomavisDateTimeFormatter1';

export function DataTableComp({ location }) {
  const { demo } = useSelector((state) => state.locationDetails);
  const types = {
    reel: { text: 'reel', bgClass: 'bg-success' },
    story: { text: 'story', bgClass: 'bg-info' },
    video: { text: 'video', bgClass: 'bg-warning' },
    multiImage: {
      text: 'multiImage',
      bgClass: 'bg-secondary text-dark'
    },
    image: {
      text: 'image',
      bgClass: 'bg-primary'
    },
    text: {
      text: 'text',
      bgClass: 'bg-primary'
    }
  };

  function buildNoDataAvailableElement() {
    return (
      <OverlayTrigger
        overlay={(props) => (
          <Tooltip {...props}>
            {t('insights_not_available_by_platform')}
          </Tooltip>
        )}
        placement="top"
      >
        <Stack>-</Stack>
      </OverlayTrigger>
    );
  }

  function chunkArray(arr, chunkSize) {
    const chunks = [];
    let i = 0;

    while (i < arr.length) {
      chunks.push(arr.slice(i, i + chunkSize));
      i += chunkSize;
    }

    return chunks;
  }

  const getPublishedOnPlatforms = (platformsArray, is_shared_post) => {
    if (demo) {
      const results = [];
      for (const key in icons.PLATFORM_ICONS) {
        results.push({ key, icon: icons.PLATFORM_ICONS[key] });
      }
      return chunkArray(results, 4);
    }
    let count = 0;
    const results = [];
    let result = [];
    const platforms = platformsArray
      ?.map((plat) => {
        if (plat.published) {
          return plat.platform;
        }
        return undefined;
      })
      .filter((a) => a !== undefined);
    platforms?.map((key) => {
      result.push({
        key,
        icon: icons.PLATFORM_ICONS[key],
        color: PLATFORM_COLORS[key]
      });
      count = count + 1;

      if (count === 4) {
        results.push(result);
        result = [];
        count = 0;
      }
    });
    if (count !== 0) {
      results.push(result);
    }
    if (is_shared_post)
      if (results[results.length - 1]?.length < 4)
        results[results.length - 1].push({
          key: 'post_types.post_is_shareable',
          icon: icons.IS_SHAREABLE_ICON
        });
      else
        results.push([
          {
            key: 'post_types.post_is_shareable',
            icon: icons.IS_SHAREABLE_ICON
          }
        ]);
    return results;
  };
  const getTypeIcon = (post) => {
    const postTypes = [];
    if (post.content_type_is_reel_post) {
      postTypes.push({
        ...types.reel,
        key: 'post_types.reel_post',
        is_badge: true
      });
    }
    if (post.content_type_is_story_post) {
      postTypes.push({
        ...types.story,
        key: 'post_types.story_post',
        is_badge: true
      });
    }
    if (post.content_type_is_video_post) {
      postTypes.push({
        ...types.video,
        key: 'post_types.video_post',
        is_badge: true
      });
    }
    if (post.content_type_is_multi_image_post) {
      postTypes.push({
        ...types.multiImage,
        key: 'post_types.multi_image_post',
        is_badge: true
      });
    }
    if (post.content_type_is_single_image_post) {
      postTypes.push({
        ...types.image,
        key: 'post_types.single_image_post',
        is_badge: true
      });
    }
    if (post.content_type_is_text_post) {
      postTypes.push({
        ...types.text,
        key: 'post_types.text_post',
        is_badge: true
      });
    }
    if (!post.created_in_lomavis) {
      postTypes.push({
        key: 'post_types.imported_post',
        icon: icons.IS_DOWNLOADABLE_ICON,
        is_badge: false
      });
    }
    return postTypes;
  };
  const dispatch = useDispatch();
  const { translate: t, currentLang } = useLocales();
  const getColumns = () => {
    return [
      {
        name: t('post'),
        show: true,
        width: '200px',
        minWidth: '200px',
        selector: (row) => (
          <div className="d-flex m-2">
            <a href={row.edit_url} className={'symbol symbol-100px me-3'}>
              <img
                src={row.preview_image_url || PlaceholderImage}
                style={{ objectFit: 'cover', objectPosition: 'center' }}
                className="img-thumbnail me-2"
              />
            </a>
          </div>
        )
      },
      {
        name: t('text'),
        show: true,
        width: '25%',
        cell: (row) => (
          <Stack
            direction={'column'}
            className="d-flex flex-column align-items-start justify-content-center"
          >
            {row.text ? (
              <div className={'py-1'}>
                <div
                  className="text-gray-700 fw-semibold fs-6"
                  style={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal'
                  }}
                  dangerouslySetInnerHTML={{ __html: row.preview_html }}
                ></div>
              </div>
            ) : (
              row.is_shared_post && (
                <div
                  className="d-flex flex-column justify-content-start align-items-start text-muted"
                  style={{
                    height: 100,
                    overflow: 'hidden',
                    whiteSpace: 'break-spaces'
                  }}
                >
                  <span className="text-gray-700 fw-semibold fs-6">
                    {t('topPosts.post_is_shared_without_comment')}
                  </span>
                </div>
              )
            )}

            {/* Platform Icons and Post Type in one row */}
            <div className="d-flex flex-row justify-content-between align-items-center mt-2 w-100">
              {/* Platform Icons */}
              <div className="d-flex flex-column">
                {getPublishedOnPlatforms(
                  row?.analytics?.data_as_json?.published,
                  row.is_shared_post
                ).map((fourIcons, i) => {
                  return (
                    <div key={i} className="d-flex flex-row">
                      {fourIcons.map((icon, i) => {
                        return (
                          <div key={i}>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{t(icon.key)}</Tooltip>}
                            >
                              <i
                                className={`${icon.icon} me-2`}
                                style={{
                                  color: icon.color,
                                  fontSize: '20px'
                                }}
                              ></i>
                            </OverlayTrigger>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>

              {/* Post Type Badges */}
              <div>
                {getTypeIcon(row).map((item) => {
                  if (item.is_badge) {
                    return (
                      <span
                        key={item.key}
                        className={`badge me-2 text-white ${item.bgClass}`}
                      >
                        {t(item.key)}
                      </span>
                    );
                  } else {
                    return (
                      <OverlayTrigger
                        key={item.key}
                        placement="top"
                        overlay={<Tooltip>{t(item.key)}</Tooltip>}
                      >
                        <i className={`${item.icon} me-2`}></i>
                      </OverlayTrigger>
                    );
                  }
                })}
              </div>
            </div>
          </Stack>
        )
      },
      {
        name: t('publishing_date'),
        show: true,
        center: true,
        selector: (row) => (
          <span className={'text-gray-700 fw-semibold fs-6'}>
            <LomavisDateTimeFormatter1 dateTimeStr={row?.publication_date} shortFormat={true} />
          </span>
        )
      },
      {
        name: t('impressions'),
        show: true,
        center: true,
        selector: (row) => {
          if (demo) {
            const { range } =
              SHORT_ANALYTICS_TABLE_DEMO_DATA_RANGES_CONFIG[IMPRESSIONS];
            return Intl.NumberFormat(currentLang.value).format(
              Math.floor(Math.random() * (range[1] - range[0] + 1) + range[0])
            );
          }

          if (row.impressions === '-' || isNaN(row.impressions)) return buildNoDataAvailableElement();

          return (
            <span className={'text-gray-700 fw-semibold fs-6'}>
              {new Intl.NumberFormat(currentLang.value).format(row.impressions)}
            </span>
          );
        }
      },
      {
        name: t('reach'),
        show: true,
        center: true,
        selector: (row) => {
          if (demo) {
            const { range } =
              SHORT_ANALYTICS_TABLE_DEMO_DATA_RANGES_CONFIG[REACTIONS]; // Using reactions for demo
            return Intl.NumberFormat(currentLang.value).format(
              Math.floor(Math.random() * (range[1] - range[0] + 1) + range[0])
            );
          }

          if (row.reach === '-' || isNaN(row.reach)) return buildNoDataAvailableElement();

          return (
            <span className={'text-gray-700 fw-semibold fs-6'}>
              {' '}
              {new Intl.NumberFormat(currentLang.value).format(row.reach)}
            </span>
          );
        }
      },
      {
        name: t('interactions'),
        show: true,
        center: true,
        selector: (row) => {
          const interactionsTooltip = (
            <Tooltip>
              <div className="text-start">
                <div>
                  {t('reactions')}: {row.reactions ? row.reactions : 0}
                </div>
                <div>
                  {t('shares')}: {row.shares ? row.shares : 0}
                </div>
                <div>
                  {t('comments')}: {row.comments ? row.comments : 0}
                </div>
                <hr />
                <div>{t('interactions')} = {t('reactions')} + {t('shares')} + {t('comments')}</div>
              </div>
            </Tooltip>
          );

          if (demo) {
            const { range } =
              SHORT_ANALYTICS_TABLE_DEMO_DATA_RANGES_CONFIG[REACTIONS];
            return Intl.NumberFormat(currentLang.value).format(
              Math.floor(Math.random() * (range[1] - range[0] + 1) + range[0])
            );
          }

          // Calculate interactions as sum of reactions, shares, and comments
          const interactions = (parseInt(row.reactions) || 0) + 
                              (parseInt(row.shares) || 0) + 
                              (parseInt(row.comments) || 0);

          if (isNaN(interactions)) return buildNoDataAvailableElement();

          return (
            <OverlayTrigger placement="top" overlay={interactionsTooltip}>
              <span className={'text-gray-700 fw-semibold fs-6'}>
                {new Intl.NumberFormat(currentLang.value).format(interactions)}
              </span>
            </OverlayTrigger>
          );
        }
      },
      {
        name: t('engagement'),
        show: true,
        center: true,
        selector: (row) => {
          const engagementTooltip = (
            <Tooltip>
              <div className="text-start">
                <div>
                  {t('reach')}: {row.reach? row.reach : 0}
                </div>
                <div>
                  {t('reactions')}: {row.reactions ? row.reactions : 0}
                </div>
                <div>
                  {t('shares')}: {row.shares ? row.shares : 0}
                </div>
                <div>
                  {t('comments')}: {row.comments ? row.comments : 0}
                </div>
                <hr />
                <div>{t('engagement_tooltip_explanation')}</div>
                <div>
                  ({t('reactions')} + {t('comments')} + {t('shares')}) /{' '}
                  {t('reach')} * 100
                </div>
              </div>
            </Tooltip>
          );

          if (demo) {
            const { range } =
              SHORT_ANALYTICS_TABLE_DEMO_DATA_RANGES_CONFIG[REACTIONS];
            return `${Math.floor(
              Math.random() * (range[1] - range[0] + 1) + range[0]
            )}%`;
          }

          if (isNaN(row.engagement)) return buildNoDataAvailableElement();

          // Ensure that 0 engagement is displayed as "0%"
          return (
            <OverlayTrigger placement="top" overlay={engagementTooltip}>
              <span className={'text-gray-700 fw-semibold fs-6'}>
                {!row.engagement ? "0%" : `${row.engagement}%`}
              </span>
            </OverlayTrigger>
          );
        }
      }
    ];
  };
  const [columns, setColumns] = useState(getColumns());
  useEffect(() => {
    setColumns(getColumns());
    setFilteredColumns(getColumns().filter((item) => item.show === true));
  }, [demo]);
  const [filteredColumns, setFilteredColumns] = useState(
    columns.filter((item) => item.show === true)
  );
  const handleColumnsChange = (i, value) => {
    const tmp = [...checkedColumns];
    tmp[i] = !tmp[i];
    setFilteredColumns(columns.filter((item, index) => tmp[index] === true));
    setCheckedColumns((prv) => {
      return tmp;
    });
  };
  // number of rows per page

  const handleRowsPerPageChange = (rowsNumber) => {
    dispatch(changeRowsNumberPerPage(rowsNumber));
  };
  // redux states
  const { isLoading, isUpdating, count, currentPagePosts, rowsNumber } =
    useSelector((state) => state.tablePosts);

  // initial load
  useEffect(() => {
    dispatch(getPosts(location));
  }, [dispatch]);

  // get selected page data
  const getSelectedPageData = (pageNumber) => {
    dispatch(getNextPosts(pageNumber, rowsNumber));
  };
  const [search, setSearch] = useState('');

  // for search
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (isMountedRef.current === false) {
      isMountedRef.current = true;
      return;
    }
    const getData = setTimeout(() => {
      dispatch(searchForPosts(location, { search }));
    }, 1000);
    return () => clearTimeout(getData);
  }, [search]);

  const [showList, setShowList] = useState(false);
  const [checkedColumns, setCheckedColumns] = useState(
    columns.map((column) => column.show)
  );
  const listRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (listRef.current && !listRef.current.contains(event.target)) {
        setShowList(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [listRef]);
  const subHeaderComponentMemo = useMemo(() => {
    const handleSearch = (e) => {
      setSearch(e.target.value);
    };
    return (
      <div className="d-flex justify-content-between w-100 py-5  align-items-center">
        <div>
          <h3
            style={{
              margin: 0,
              paddingLeft: 0,
              fontWeight: 500,
              fontSize: '1.275rem'
            }}
          >
            {t('your_last_posts')}
          </h3>
        </div>
        <div className="d-flex flex-row align-items-center" ref={listRef}>
          <div className=" mx-1 " style={{ zIndex: 10 }}>
            <Button
              className="fs-8"
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowList((prevState) => !prevState);
              }}
            >
              <i className="fa-duotone fa-filter-list"></i>&nbsp;{t('columns')}
            </Button>
            {showList && (
              <div className="d-flex flex-column position-absolute z-10 bg-white rounded fit-content p-2 border">
                {columns.map((item, i) => (
                  <div key={item.name} className="m-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value="1"
                      id="flexCheckDefault"
                      onChange={() => handleColumnsChange(i, !item.show)}
                      checked={checkedColumns[i]}
                    />
                    &nbsp; {item.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="d-felx justify-content-end">
            <input
              className="form-control form-control-sm "
              type="text"
              onChange={handleSearch}
              placeholder={t('search')}
            />
          </div>
        </div>
      </div>
    );
  }, [search, showList, checkedColumns, filteredColumns, listRef]);
  const CustomNoDataComponent = () => (
    <div className="custom-no-data-message m-2 p-2">
      {t('no_posts_have_been_published_with_lomavis')}
    </div>
  );

  return (
    <div className="card rounded shadow p-2">
      <DataTable
        noDataComponent={<CustomNoDataComponent />}
        columns={filteredColumns}
        data={currentPagePosts}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        pagination
        paginationServer
        paginationTotalRows={count}
        onChangePage={getSelectedPageData}
        paginationComponentOptions={{
          rangeSeparatorText: t('of'),
          rowsPerPageText: t('rows_per_page'),
          showFirstLastPageButtons: false
        }}
        paginationIconLastPage={<></>} // hides the last page button
        progressPending={isLoading}
        paginationPerPage={rowsNumber}
        onChangeRowsPerPage={handleRowsPerPageChange}
        progressComponent={<div>{t('loading')}</div>}
        paginationRowsPerPageOptions={[5, 10, 15, 20]}
        paginationShowLastPageJump={false}
      />
      <div className="d-flex justify-content-end">
        {isUpdating ? <IsUpdatingIndicator /> : ''}
      </div>
    </div>
  );
}

DataTableComp.propTypes = {
  location: PropTypes.string.isRequired
};
